import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/PersonAddAlt1';
import SaveIcon from '@mui/icons-material/Save';
import { storeOperatorService, StoreOperatorRecord } from '../services/storeOperatorService';
import { storeService } from '../services/storeService';
import { Store } from '../models/Store';

const StoreOperators: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState<StoreOperatorRecord[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [uidInput, setUidInput] = useState('');
  const [enabledInput, setEnabledInput] = useState(true);
  const [storeIdsInput, setStoreIdsInput] = useState<string[]>([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState<string>('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info'>('info');

  const [editRows, setEditRows] = useState<Record<string, { enabled: boolean; storeIds: string[] }>>({});

  const sortedOps = useMemo(() => {
    return [...operators].sort((a, b) => {
      const ae = (a.email || '').toLowerCase();
      const be = (b.email || '').toLowerCase();
      if (ae && be) return ae.localeCompare(be);
      if (ae) return -1;
      if (be) return 1;
      return a.uid.localeCompare(b.uid);
    });
  }, [operators]);

  const storeById = useMemo(() => {
    return new Map(stores.map((s) => [s.id, s]));
  }, [stores]);

  const resolveStoreSelection = (ids: string[]): Store[] => {
    return ids.map((id) => storeById.get(id)).filter(Boolean) as Store[];
  };

  const getStoreLabel = (store: Store) => {
    const name = store.name?.trim();
    return name ? `${name} — ${store.id}` : store.id;
  };

  const getStoreChipLabel = (id: string) => {
    return storeById.get(id)?.name || id;
  };

  const showSnack = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [operatorsResult, storesResult] = await Promise.allSettled([
      storeOperatorService.list(),
      storeService.getAllStores(),
    ]);

    if (operatorsResult.status === 'fulfilled') {
      const list = operatorsResult.value;
      setOperators(list);
      const init: Record<string, { enabled: boolean; storeIds: string[] }> = {};
      list.forEach((o) => {
        init[o.uid] = { enabled: !!o.enabled, storeIds: o.storeIds || [] };
      });
      setEditRows(init);
    } else {
      console.error('listStoreOperators error:', operatorsResult.reason);
      showSnack(
        operatorsResult.reason instanceof Error
          ? operatorsResult.reason.message
          : 'Erreur lors du chargement des opérateurs',
        'error'
      );
    }

    if (storesResult.status === 'fulfilled') {
      setStores(storesResult.value);
    } else {
      console.error('getAllStores error:', storesResult.reason);
      showSnack(
        storesResult.reason instanceof Error
          ? storesResult.reason.message
          : 'Erreur lors du chargement des magasins',
        'error'
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddByEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      showSnack('Email invalide.', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await storeOperatorService.addByEmail(email, enabledInput, storeIdsInput);
      showSnack(`Opérateur ajouté: ${res.email} (${res.uid})`, 'success');
      setEmailInput('');
      setStoreIdsInput([]);
      await load();
    } catch (e: any) {
      console.error('addStoreOperatorByEmail error:', e);
      showSnack(e?.message || 'Echec ajout opérateur par email', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddByUid = async () => {
    const uid = uidInput.trim();
    if (!uid) {
      showSnack('UID requis.', 'error');
      return;
    }
    setLoading(true);
    try {
      await storeOperatorService.addByUid(uid, enabledInput, storeIdsInput);
      showSnack(`Opérateur ajouté (uid): ${uid}`, 'success');
      setUidInput('');
      setStoreIdsInput([]);
      await load();
    } catch (e: any) {
      console.error('addStoreOperatorByUid error:', e);
      showSnack(e?.message || 'Echec ajout opérateur par UID', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (uid: string) => {
    const row = editRows[uid];
    if (!row) return;
    setLoading(true);
    try {
      await storeOperatorService.update(uid, { enabled: row.enabled, storeIds: row.storeIds });
      showSnack(`Opérateur mis à jour: ${uid}`, 'success');
      await load();
    } catch (e: any) {
      console.error('updateStoreOperator error:', e);
      showSnack(e?.message || 'Echec mise à jour opérateur', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (uid: string) => {
    if (!uid) return;
    setLoading(true);
    try {
      await storeOperatorService.remove(uid);
      showSnack(`Opérateur supprimé: ${uid}`, 'success');
      await load();
    } catch (e: any) {
      console.error('removeStoreOperatorByUid error:', e);
      showSnack(e?.message || 'Echec suppression opérateur', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Gestion des Opérateurs Magasin
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Autorise des comptes à utiliser l’app Magasin. Active-les ou désactive-les, puis choisis
        les magasins auxquels ils ont accès.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Email"
            size="small"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            placeholder="store.operator@example.com"
          />
          <TextField
            label="UID"
            size="small"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            placeholder="UID Firebase Auth"
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={stores}
            value={resolveStoreSelection(storeIdsInput)}
            onChange={(_, value) => setStoreIdsInput(value.map((v) => v.id))}
            getOptionLabel={getStoreLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Magasins autorisés"
                size="small"
                placeholder="Choisir un magasin"
              />
            )}
            sx={{ flex: 2, minWidth: 260 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Switch checked={enabledInput} onChange={(e) => setEnabledInput(e.target.checked)} />}
            label="Activer immédiatement (enabled=true)"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Ajouter par email">
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddByEmail}
                disabled={loading}
              >
                Ajouter par email
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Ajouter par UID">
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddByUid}
                disabled={loading}
              >
                Ajouter par UID
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Liste des Opérateurs</Typography>
          <Typography variant="body2" color="text.secondary">{sortedOps.length} opérateur(s)</Typography>
        </Stack>

        <Box sx={{ mt: 1 }}>
          {sortedOps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aucun opérateur trouvé.</Typography>
          ) : (
            sortedOps.map((op) => {
              const row = editRows[op.uid] || { enabled: !!op.enabled, storeIds: op.storeIds || [] };
              return (
                <Stack
                  key={op.uid}
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={1.5}
                  sx={{
                    py: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
                      {op.email || 'Email inconnu'} — {op.uid}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {(op.storeIds || []).slice(0, 6).map((s) => (
                        <Chip key={s} size="small" label={getStoreChipLabel(s)} />
                      ))}
                      {(op.storeIds || []).length > 6 && <Chip size="small" label={`+${(op.storeIds || []).length - 6}`} />}
                    </Stack>
                  </Box>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={stores}
                    value={resolveStoreSelection(row.storeIds)}
                    onChange={(_, value) => setEditRows(prev => ({ ...prev, [op.uid]: { ...row, storeIds: value.map((v) => v.id) } }))}
                    getOptionLabel={getStoreLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Magasins"
                        size="small"
                      />
                    )}
                    sx={{ flex: 2, minWidth: 260 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={row.enabled}
                        onChange={(e) => setEditRows(prev => ({ ...prev, [op.uid]: { ...row, enabled: e.target.checked } }))}
                      />
                    }
                    label={row.enabled ? 'Activé' : 'Désactivé'}
                  />

                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Enregistrer">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => handleUpdate(op.uid)}
                          disabled={loading}
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => handleRemove(op.uid)}
                          disabled={loading}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })
          )}
        </Box>
      </Paper>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoreOperators;
